/**
 * The vendored converter, checked before first use: the file must be the
 * pinned one, speak the pinned ABI, and answer the canary with the pinned sha.
 * `src/wasm/mce_idl/pin.json` holds all three, so a new converter is one commit.
 */
import { sha256Hex } from "../../flatbuffers/sha256";
import pin from "../../wasm/mce_idl/pin.json";
import { callMceIdl, mceIdlAbiVersion } from "./abi";
import { asString, unpackResult } from "./messages";

/** A converter that passed its checks. */
export interface MceIdlEngine {
  call(request: Uint8Array): Promise<Uint8Array>;
}

/** The running converter is not the one this page was built against. */
export class MceIdlSkew extends Error {
  constructor(detail: string) {
    super(`MCE IDL converter check failed: ${detail}`);
    this.name = "MceIdlSkew";
  }
}

/** Compile and check the vendored converter. */
export async function loadMceIdlEngine(
  wasmBytes: Uint8Array,
  canary: Uint8Array,
): Promise<MceIdlEngine> {
  const actual = await sha256Hex(wasmBytes);
  if (actual !== pin.wasmSha256) {
    throw new MceIdlSkew(`wasm sha ${actual}, pinned ${pin.wasmSha256}`);
  }
  const module = await WebAssembly.compile(wasmBytes.slice());
  const abi = await mceIdlAbiVersion(module);
  if (abi !== pin.abiVersion) {
    throw new MceIdlSkew(`ABI ${abi}, pinned ${pin.abiVersion}`);
  }
  const sha = asString(unpackResult(await callMceIdl(module, canary)).canonicalSha256);
  if (sha !== pin.canaryCanonicalSha256) {
    throw new MceIdlSkew(`canary sha ${sha}, pinned ${pin.canaryCanonicalSha256}`);
  }
  return { call: (request) => callMceIdl(module, request) };
}
